import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAccessToken } from '@/redux/reducers/auth.reducer'
import { createElaraTransport } from '../core/elara-transport'
import type { ElaraSendOptions, ElaraToolCallDescriptor } from '../core/elara-transport'
import type { ClientActionEvent } from '../types'

/** Per-stream handlers — the chat page wires these into its message list. */
export interface ElaraStreamHandlers {
    onHistory?: (chatHistory: any[]) => void
    onDelta?: (token: string) => void
    onToolCall?: (calls: ElaraToolCallDescriptor[]) => void
    onToolEnd?: (calls: ElaraToolCallDescriptor[], toolResult?: string) => void
    onSessionId?: (sessionId: string) => void
    onComplete?: (sessionId: string | null, output: string) => void
    onError?: (error: 'parse' | 'connection' | string) => void
}

export interface UseElaraChatStreamArgs {
    /** Envelope fields merged into every request (page context, platform meta, ...) */
    getSendOptions: () => Omit<ElaraSendOptions, 'sessionId'>
    /** A `ui_action` tool result from the agent — run it on the page (navigate, open dialog, …). */
    onClientAction?: (event: ClientActionEvent) => void
}

/**
 * React binding of the shared ELARA SSE transport for the full chat page / ChatModal / side panel.
 * One transport instance per component; `startStream` always carries an explicit session id
 * (`null` = start a new session).
 */
export function useElaraChatStream({ getSendOptions, onClientAction }: UseElaraChatStreamArgs) {
    const accessToken = useAppSelector(selectAccessToken)
    const accessTokenRef = useRef<string | null>(accessToken ?? null)
    useEffect(() => { accessTokenRef.current = accessToken ?? null }, [accessToken])

    const getSendOptionsRef = useRef(getSendOptions)
    useEffect(() => { getSendOptionsRef.current = getSendOptions }, [getSendOptions])
    const onClientActionRef = useRef(onClientAction)
    useEffect(() => { onClientActionRef.current = onClientAction }, [onClientAction])

    const handlersRef = useRef<ElaraStreamHandlers>({})
    const [isStreaming, setIsStreaming] = useState(false)

    const transport = useMemo(
        () => createElaraTransport({
            getAccessToken: () => accessTokenRef.current,
            onProcessingChange: setIsStreaming,
            onHistory: (h) => handlersRef.current.onHistory?.(h),
            onStreamToken: (token) => handlersRef.current.onDelta?.(token),
            onToolEvent: (event, tools, toolResult) => {
                if (event === 'call' && tools.length > 0) handlersRef.current.onToolCall?.(tools)
                if (event === 'end') handlersRef.current.onToolEnd?.(tools, toolResult)
            },
            onClientAction: (event) => onClientActionRef.current?.(event),
            onSessionId: (id) => handlersRef.current.onSessionId?.(id),
            onComplete: (sessionId, output) => handlersRef.current.onComplete?.(sessionId, output),
            onError: (error) => handlersRef.current.onError?.(error),
        }),
        []
    )

    useEffect(() => () => transport.close(), [transport])

    const startStream = useCallback(
        (prompt: string, sessionId: string | null, handlers: ElaraStreamHandlers = {}) => {
            handlersRef.current = handlers
            transport.send(prompt, { ...getSendOptionsRef.current(), sessionId })
        },
        [transport]
    )

    const closeStream = useCallback(() => transport.close(), [transport])

    return { isStreaming, startStream, closeStream }
}

export default useElaraChatStream
